import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Theme, Typography, Divider, Grid } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { OrderProduct, ShopPublicInfo, Shopper, OrderObject } from 'common/types';
import moment from 'moment-timezone';
import bwipjs from 'bwip-js';
import classNames from 'classnames';
import {
	getEarliestProductStartTime,
	getLatestProductEndTime,
	getTotalRentalDurationFromProducts,
	getDurationString,
} from 'common/utils/dateCalculations';
import ReceiptProductInfo from './ReceiptProductInfo';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import { getPricingString } from 'common/utils/common';
import { getResponsiblePersonDetails } from 'common/utils/newRentalUtils';
import useShopFormat from 'hooks/useShopFormat';
import { useTranslation } from 'services/localization/useTranslation';
import errorHandler from 'common/services/errorHandler';
import { isLiftTicketProduct } from 'common/utils/liftTicketUtils';

import * as ViewSelectors from 'selectors/ViewSelectors';
import { getTotalProductPricing } from 'common/modules/orders';
import { PriceBreakdown } from 'common/components/molecules/PriceBreakdown';
import { getItemsPayments } from 'common/modules/atoms/paymentAmounts';
import RentleLogo from 'common/components/RentleLogo';

export interface Props {
	shop: ShopPublicInfo;
	rental: OrderObject;
	shopperId: string;
	ref: React.MutableRefObject<any>;
	barCode: string;
}

const ShopperReceipt = (props: Props) => {
	const { t } = useTranslation();
	const barCode = props.barCode.trim();
	const { rental, shop, shopperId } = props;
	const { rentalInfo, products, shoppers } = rental;
	const shopper = shoppers.find((s) => s.id === shopperId)!;
	const shopperProducts = rental.products.filter((p) => p.shopperId === shopperId);
	const pricing = getTotalProductPricing(shopperProducts);
	const chargePayment = getItemsPayments(shopperProducts).charge;
	const currency = getCurrencyObjectFromCurrencyCode(rentalInfo.pricing.currency, shop.currency);
	const { localFormat } = useShopFormat();
	const lang = useSelector(ViewSelectors.lang);
	const classes = useStyles();

	useEffect(() => {
		const createBarCode = async () => {
			if (rentalInfo.id) {
				try {
					(bwipjs as any).toCanvas(`barcode_${shopperId}`, {
						bcid: 'code128',
						text: barCode,
						scale: 3,
						height: 10,
						includetext: false,
						textxalign: 'center',
					});
				} catch (e) {
					errorHandler.report(e);
				}
			}
		};
		createBarCode();
	}, [barCode, rentalInfo.id, shopperId]);

	const renderTopSection = (shopper: Shopper, shopperProducts: OrderProduct[]) => {
		const responsiblePerson = getResponsiblePersonDetails(rentalInfo.responsiblePerson, shoppers);
		const name = shopper.firstName + ' ' + shopper.lastName;
		const responsiblePersonString = responsiblePerson
			? responsiblePerson.firstName + ' ' + responsiblePerson.lastName
			: '';
		const differentResponsiblePerson = name !== responsiblePersonString;
		const startDate = getEarliestProductStartTime(products) || rentalInfo.startDate || undefined;
		const endDate = getLatestProductEndTime(products) || undefined;
		const rentalDuration = getTotalRentalDurationFromProducts(products);
		const durationString = getDurationString(rentalDuration, 'long', lang, t);
		const sameDay = moment(startDate).isSame(moment(endDate), 'day');
		const isTicketsOnly = shopperProducts.every(isLiftTicketProduct);
		const dateString = isTicketsOnly
			? `${localFormat(startDate, 'ddd D.M.YYYY')}`
			: sameDay
			? `${localFormat(startDate, 'ddd D.M.YYYY HH:mm')} - ${localFormat(endDate, 'HH:mm')}`
			: `${localFormat(startDate, 'ddd D.M.YYYY HH:mm')} - ${localFormat(
					endDate,
					'ddd D.M.YYYY HH:mm',
			  )}`;
		return (
			<div>
				<Typography className={classNames(classes.receiptFont, classes.lgFont)} align="center">
					{name}
				</Typography>
				<Typography className={classNames(classes.receiptFont, classes.mdFont)} align="center">
					{differentResponsiblePerson &&
						responsiblePersonString &&
						t('receipt.partOfOrder', {
							responsiblePerson: responsiblePersonString,
							defaultValue: 'Part of order: {{responsiblePerson}}',
						})}
				</Typography>
				<Typography className={classes.receiptFont} variant="body2" align="center">
					{durationString}
				</Typography>
				<Typography className={classes.receiptFont} variant="body2" align="center">
					{dateString}
				</Typography>
			</div>
		);
	};

	const renderProductSection = (product: OrderProduct) => {
		return (
			<React.Fragment key={product.id}>
				<Grid
					key={product.id}
					className={product.set ? classes.spaceAbove : classes.spaceBetween}
					container
					justifyContent="space-between"
				>
					<Grid item xs>
						<ReceiptProductInfo product={product} />
					</Grid>
					<Grid item className={classes.noFlex}>
						<Typography className={classes.receiptFont} variant="body2">
							{getPricingString(product.pricing.listPrice, currency)}
						</Typography>
					</Grid>
				</Grid>
				{product.set &&
					product.setProducts.map((setProduct, i) => {
						return (
							<Grid key={'set' + i}>
								<Grid item xs>
									<ReceiptProductInfo product={setProduct} setProduct={true} />
								</Grid>
							</Grid>
						);
					})}
			</React.Fragment>
		);
	};

	const renderFooter = () => {
		return (
			<div>
				<Grid container justifyContent="center">
					<Grid item xs>
						<RentleLogo width="100%" type="black" />
					</Grid>
				</Grid>
				<Typography
					className={classNames(classes.receiptFont, classes.idCaption)}
					variant="body2"
					align="center"
				>
					{rentalInfo.id}
				</Typography>
				<Grid container justifyContent="space-between" className={classes.spacingTopSmall}>
					<Grid item>
						<Typography className={classes.receiptFont} align="center" variant="caption">
							{'Powered by Rentle'}
						</Typography>
					</Grid>
					<Grid item>
						<Typography className={classes.receiptFont} align="center" variant="caption">
							{'www.rentle.io'}
						</Typography>
					</Grid>
				</Grid>
			</div>
		);
	};

	return (
		<div className={classes.receiptContainer}>
			<div>
				<Typography className={classNames(classes.receiptFont, classes.mdFont)} align="center">
					{t('receipt.orderConfirmation', 'Order confirmation')}
				</Typography>
			</div>
			<div className={classes.spacingTopXSmall}>
				<Typography
					className={classNames(classes.receiptFont, classes.smFont)}
					variant="body1"
					align="center"
				>
					{shop.name}
				</Typography>
				<Typography
					className={classNames(classes.receiptFont, classes.smFont)}
					variant="body2"
					align="center"
				>
					{shop.visitingAddress!.address || ''}
				</Typography>
				<Typography
					className={classNames(classes.receiptFont, classes.smFont)}
					variant="body2"
					align="center"
				>
					{shop.phone}
				</Typography>
			</div>
			<div className={classes.spacingTopSmall}>{renderTopSection(shopper, shopperProducts)}</div>
			<Divider
				classes={{
					root: classes.divider,
				}}
			/>
			<div className={classes.spacingTopSmall}>
				{shopperProducts.map(renderProductSection)}
				<PriceBreakdown
					pricing={pricing}
					shopCurrency={currency}
					t={t}
					rows={['manualDiscount', 'discountCodes']}
					lang={lang}
				/>
				<Divider
					classes={{
						root: classes.divider,
					}}
				/>
			</div>

			<div className={classes.spacingTopSmall}>
				<PriceBreakdown
					pricing={pricing}
					payment={chargePayment}
					shopCurrency={currency}
					t={t}
					rows={['subtotal', 'taxes', 'total', 'paid']}
					lang={lang}
				/>
			</div>
			<div className={classNames(classes.spacingTopSmall, classes.textCenter)}>
				<canvas className={classes.barcode} id={`barcode_${shopperId}`}></canvas>
				<Typography
					variant="body2"
					gutterBottom
					className={classNames(classes.receiptFont, classes.smFont)}
					align="center"
				>
					{barCode}
				</Typography>
			</div>
			<div className={classes.spacingTopSmall}>{renderFooter()}</div>
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		receiptContainer: {
			width: '92%',
			maxWidth: 500,
			marginLeft: 'auto',
			marginRight: 'auto',
			padding: '4mm 2mm',
			'&*': {
				fontFamily: 'arial',
				'text-rendering': 'geometricPrecision',
				'-webkit-font-smoothing': 'subpixel-antialiased',
			},
			'& p': {
				margin: '2px 0',
			},
			color: 'black !important',
			fontWeight: 500,
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
		spacingTopXSmall: {
			paddingTop: '8px',
		},
		spacingTopSmall: {
			paddingTop: '16px',
		},
		spacingTopLarge: {
			paddingTop: '32px',
		},
		receiptFont: {
			fontFamily: ['arial'].join(','),
			color: 'black !important',
			fontWeight: 500,
			fontSize: 14,
			lineHeight: 1.2,
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
		smFont: {
			fontSize: 12,
		},
		lgFont: {
			fontWeight: 600,
			fontSize: 25,
		},
		mdFont: {
			fontWeight: 600,
			fontSize: 20,
		},
		divider: {
			border: '0 none',
			borderTop: '1px dashed black',
			background: 'none',
			height: 1,
			marginTop: '16px',
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
		textCenter: {
			textAlign: 'center',
		},
		barcode: {
			width: '100%',
			padding: '0mm 2mm',
			marginLeft: 'auto',
			marginRight: 'auto',
			maxWidth: 320,
			maxHeight: 48, // 15% of max-width
			color: theme.palette.common.black,
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
			marginBottom: 4,
		},
		idCaption: {
			paddingTop: 4,
			fontSize: 10,
		},
		noFlex: {
			flex: '0 0 auto',
			paddingLeft: 16,
		},
		spaceBetween: {
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
		},
		spaceAbove: {
			marginTop: theme.spacing(1),
		},
	}),
);

// Must export a class to work with react-to-print
class ShopperReceiptContainer extends React.Component<Props> {
	render() {
		return (
			<div>
				<ShopperReceipt {...this.props} />
			</div>
		);
	}
}

export default ShopperReceiptContainer;
